import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { components } from 'react-select';
import classNames from 'classnames';
import BaseSelectField from 'components/essentials/BaseSelectField';
import JBImage from 'components/essentials/JBImage';
import { PDP_READYTOSHIP_TAB_ID } from 'commons/constants';
import usePdpSelectedCustomizerTab from '../state/pdpSelectedCustomizerTab/usePdpSelectedCustomizerTab';
import usePDPOptions from '../state/context/usePDPOptions';
import getOrientationOptionIconV2 from '../helpers/getOrientationOptionIconV2';
import getProductOptionImageUrl from '../helpers/getProductOptionImageUrl';
export const OptionValueIcon = t0 => {
  const $ = _c(9);
  const {
    data,
    loading: t1
  } = t0;
  const loading = t1 === undefined ? "lazy" : t1;
  const {
    customItem,
    image,
    productOptionImage
  } = data;
  const [hasProductOptionImageError, setHasProductOptionImageError] = useState(false);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setHasProductOptionImageError(false);
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== productOptionImage) {
    t3 = [productOptionImage];
    $[1] = productOptionImage;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  useEffect(t2, t3);
  const showProductOptionImage = !!productOptionImage && !hasProductOptionImageError;
  if (!showProductOptionImage && !customItem && !image) {
    return null;
  }
  let t4;
  if ($[3] !== customItem || $[4] !== image || $[5] !== loading || $[6] !== productOptionImage || $[7] !== showProductOptionImage) {
    t4 = <div className="flex-shrink-0 h-20 w-20 flex items-center justify-center [&_svg]:h-20 [&_svg]:w-20">{showProductOptionImage ? <img src={productOptionImage} alt="" data-testid="product-option-image" height="80" width="80" loading={loading} className="h-20 w-20 object-contain" onError={() => setHasProductOptionImageError(true)} /> : customItem ? <React.Suspense fallback={<div className="w-20 h-20" />}>{customItem}</React.Suspense> : <JBImage src={image} height="80" width="80" className="object-contain" />}</div>;
    $[3] = customItem;
    $[4] = image;
    $[5] = loading;
    $[6] = productOptionImage;
    $[7] = showProductOptionImage;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};
OptionValueIcon.propTypes = {
  data: PropTypes.shape({
    customItem: PropTypes.node,
    image: PropTypes.string,
    productOptionImage: PropTypes.string
  }).isRequired,
  loading: PropTypes.oneOf(['lazy', 'eager'])
};
const ImageAndTextRenderer = t0 => {
  const $ = _c(16);
  const {
    data,
    isSelected,
    loading
  } = t0;
  const t1 = !isSelected;
  let t2;
  if ($[0] !== isSelected || $[1] !== t1) {
    t2 = classNames("flex flex-row items-center space-x-3 w-full px-2", {
      "text-white": isSelected,
      "text-gray-dark": t1
    });
    $[0] = isSelected;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== data || $[4] !== loading) {
    t3 = <OptionValueIcon data={data} loading={loading} />;
    $[3] = data;
    $[4] = loading;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = !isSelected;
  let t5;
  if ($[6] !== isSelected || $[7] !== t4) {
    t5 = classNames("text-lg font-bold", {
      "text-white": isSelected,
      "text-gray-dark": t4
    });
    $[6] = isSelected;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== data.label || $[10] !== t5) {
    t6 = <span className={t5}>{data.label}</span>;
    $[9] = data.label;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t2 || $[13] !== t3 || $[14] !== t6) {
    t7 = <div className={t2}>{t3}{t6}</div>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
ImageAndTextRenderer.propTypes = {
  data: PropTypes.shape({
    customItem: PropTypes.node,
    image: PropTypes.string,
    label: PropTypes.string,
    productOptionImage: PropTypes.string
  }).isRequired,
  isSelected: PropTypes.bool,
  loading: PropTypes.oneOf(['lazy', 'eager'])
};
const Option = props => {
  const $ = _c(6);
  let t0;
  if ($[0] !== props.data || $[1] !== props.isSelected) {
    t0 = <ImageAndTextRenderer data={props.data} isSelected={props.isSelected} />;
    $[0] = props.data;
    $[1] = props.isSelected;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== props || $[4] !== t0) {
    t1 = <div className="flex border-b border-gray-light6 cursor-pointer relative hover:shadow-none hover:bg-gray-light7 last:border-b-0 w-full py-1"><components.Option {...props} className="w-full !cursor-pointer !p-0">{t0}</components.Option></div>;
    $[3] = props;
    $[4] = t0;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
Option.propTypes = {
  data: PropTypes.object
};
const SingleValue = props => {
  const $ = _c(11);
  const isDisabled = props.data?.isDisabled;
  let t0;
  if ($[0] !== isDisabled) {
    t0 = classNames("flex-1", {
      "cursor-not-allowed [&_.react-select\\_\\_single-value]:text-gray-light4": isDisabled
    });
    $[0] = isDisabled;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props.data) {
    t1 = <ImageAndTextRenderer data={props.data} loading="eager" />;
    $[2] = props.data;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== isDisabled || $[5] !== props || $[6] !== t1) {
    t2 = <components.SingleValue isDisabled={isDisabled} {...props} className="!absolute inset-y-0 flex items-center">{t1}</components.SingleValue>;
    $[4] = isDisabled;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t0 || $[9] !== t2) {
    t3 = <div className={t0}>{t2}</div>;
    $[8] = t0;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};
SingleValue.propTypes = {
  data: PropTypes.object
};
const customComponents = {
  Option,
  SingleValue
};
const PDPImageTextDropdownOption = ({
  optionId
}) => {
  const {
    data = {},
    actions
  } = usePDPOptions();
  const pdpSelectedCustomizerTab = usePdpSelectedCustomizerTab();
  const {
    options = [],
    selectedOptions = {},
    stock = {}
  } = data;
  const {
    stockOptions = [],
    stockOptionsDisabledStatus = {}
  } = stock;
  const values = useMemo(() => {
    const finalOptions = pdpSelectedCustomizerTab === PDP_READYTOSHIP_TAB_ID ? stockOptions : options;
    if (!finalOptions || !optionId || !selectedOptions) {
      return [];
    }
    const selectedOptionVal = finalOptions.find(option => option.id === optionId);
    if (!selectedOptionVal) {
      return [];
    }
    const updatedValues = selectedOptionVal.values.map(optionValue => {
      const {
        image = null,
        customItem = null
      } = getOrientationOptionIconV2(optionId, optionValue.sku);
      return {
        value: optionValue.id,
        label: optionValue.value.trim(),
        image: image || optionValue.image,
        productOptionImage: getProductOptionImageUrl(optionValue.product_option_image),
        customItem,
        isDisabled: pdpSelectedCustomizerTab === PDP_READYTOSHIP_TAB_ID && stockOptionsDisabledStatus[optionValue.id]
      };
    });
    return updatedValues;
  }, [options, stockOptions, stockOptionsDisabledStatus, selectedOptions, pdpSelectedCustomizerTab, optionId]);
  const defaultSelectionValue = useMemo(() => {
    if (!values?.length || !data?.selectedOptionsExtended) {
      return {};
    }
    return values.find(val => `${val.value}` === `${data.selectedOptionsExtended?.[optionId]?.value?.id}`) || {};
  }, [values, data?.selectedOptionsExtended, optionId]);
  const selectedOption = useMemo(() => values.find(v => `${v.value}` === `${selectedOptions?.[optionId]}`) || null, [values, selectedOptions, optionId]);
  const handleChange = useCallback(optionValue_0 => {
    actions.onOptionValueSelection(optionId, optionValue_0.value);
  }, [actions, optionId]);
  const optionName = options?.find(option_0 => option_0.id === optionId)?.name;
  return <div className="flex flex-col w-full">
      <div className="text-sm font-bold w-full text-gray-dark p-0 mt-0 mx-0 mb-1.5">
        {optionName ? `Select ${optionName}` : ''}
      </div>

      <div>
        {values.length ? <BaseSelectField className="pdp-image-dropdown-field h-28 [&_.react-select\_\_control]:h-full [&_.react-select\_\_value-container]:h-full" options={values} onChange={handleChange} value={selectedOption} customComponents={customComponents} defaultValue={defaultSelectionValue} ariaLabel="Options Dropdown" /> : null}
      </div>
    </div>;
};
PDPImageTextDropdownOption.propTypes = {
  optionId: PropTypes.number
};
export default PDPImageTextDropdownOption;